import type { StorefrontSiteCode } from "@/lib/storefront-site";
import type { FooterData } from "@/lib/storefront-types";

const ALL_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

// Per-site SEO identity. Kept inline so this module stays dependency-free and
// unit-testable; brand names and canonical domains are stable constants.
const SITE_SEO_IDENTITY = {
  asorockfood: {
    brandName: "Aso Rock Food",
    origin: "https://www.asorockfood.com",
    logoPath: "/logo-desktop.svg",
  },
  asorockfrozen: {
    brandName: "Aso Rock Frozen",
    origin: "https://www.asorockfrozen.com",
    logoPath: "/logo-desktop-frozen.svg",
  },
} as const;

// 9-10 Bradbury Street, Dalston, London N16 8JN (the dine-in restaurant).
// Coordinates are fixed here rather than derived from the CMS footer because
// geocoding needs an external service; update alongside any address change.
const RESTAURANT_GEO = {
  latitude: 51.5486,
  longitude: -0.0761,
} as const;

const UK_POSTCODE_PATTERN = /\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/i;

/** Pulls a UK postcode out of the CMS footer's free-text store address. */
export function extractUkPostcode(address: string): string | null {
  const match = address.match(UK_POSTCODE_PATTERN);
  if (!match) {
    return null;
  }
  return `${match[1].toUpperCase()} ${match[2].toUpperCase()}`;
}

/**
 * Serialises JSON-LD for safe embedding inside a <script> tag. Escaping `<`,
 * `>` and `&` neutralises any `</script>` sequence that could otherwise break
 * out of the tag — an XSS vector if a CMS field ever contains markup.
 */
export function serializeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026");
}

function normalizeSocialUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function collectSameAs(footer: FooterData | null): string[] {
  if (!footer) {
    return [];
  }
  return footer.socialLinks
    .map((link) => normalizeSocialUrl(link.url))
    .filter((url): url is string => Boolean(url));
}

/**
 * Builds the baseline Schema.org JSON-LD for a storefront: a `Restaurant` for
 * the London dine-in/delivery site, an `OnlineStore` for the nationwide
 * frozen-shipping site. Both carry the site-wide `@id` `<origin>/#organization`
 * that other nodes (WebSite publisher, borough Service provider) reference.
 * Contact details come from the CMS footer feed.
 *
 * `imageUrl` is an optional real photo (an absolute URL, e.g. the page's CMS
 * SEO image) for the Restaurant's `image` property — Google's LocalBusiness
 * guidance wants a photo, which the SVG `logo` cannot satisfy, so when no
 * photo is supplied the property is omitted rather than reusing the logo.
 */
export function buildStorefrontStructuredData(
  siteCode: StorefrontSiteCode,
  footer: FooterData | null,
  imageUrl?: string | null,
): Record<string, unknown> {
  const { brandName, origin, logoPath } = SITE_SEO_IDENTITY[siteCode];
  const contact = footer?.contact;
  const sameAs = collectSameAs(footer);

  const telephone = contact?.telephoneNumber?.trim() || null;
  const email = contact?.emailAddress?.trim() || null;

  if (siteCode === "asorockfrozen") {
    return {
      "@context": "https://schema.org",
      // OnlineStore (an Organization subtype): the Frozen site is a UK-wide
      // e-commerce shop, not a local business with premises.
      "@type": "OnlineStore",
      "@id": `${origin}/#organization`,
      name: brandName,
      url: origin,
      logo: `${origin}${logoPath}`,
      areaServed: "GB",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "customer service",
        areaServed: "GB",
        availableLanguage: "English",
        ...(telephone ? { telephone } : {}),
        ...(email ? { email } : {}),
      },
      ...(sameAs.length > 0 ? { sameAs } : {}),
    };
  }

  const postalCode = contact?.storeAddress
    ? extractUkPostcode(contact.storeAddress)
    : null;

  return {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "@id": `${origin}/#organization`,
    name: brandName,
    url: origin,
    ...(imageUrl ? { image: imageUrl } : {}),
    logo: `${origin}${logoPath}`,
    hasMenu: `${origin}/menu`,
    servesCuisine: ["Nigerian", "West African"],
    priceRange: "££",
    areaServed: "London, United Kingdom",
    acceptsReservations: true,
    geo: {
      "@type": "GeoCoordinates",
      latitude: RESTAURANT_GEO.latitude,
      longitude: RESTAURANT_GEO.longitude,
    },
    ...(telephone ? { telephone } : {}),
    ...(email ? { email } : {}),
    address: {
      "@type": "PostalAddress",
      ...(contact?.storeAddress
        ? {
            streetAddress: contact.storeAddress
              .replace(/\s*\n\s*/g, ", ")
              .trim(),
          }
        : {}),
      addressLocality: "London",
      ...(postalCode ? { postalCode } : {}),
      addressCountry: "GB",
    },
    // Restaurant opening hours: 11:00–22:00, daily. (The website accepts
    // orders 24/7 — openingHoursSpecification reflects kitchen/service hours.)
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ALL_WEEK,
        opens: "11:00",
        closes: "22:00",
      },
    ],
    ...(sameAs.length > 0 ? { sameAs } : {}),
  };
}

/**
 * WebSite node for the home page. `publisher` references the organization
 * node emitted alongside it by `@id`. Deliberately no SearchAction — the
 * storefronts have no search results page.
 */
export function buildWebSiteSchema(
  origin: string,
  siteName: string,
): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${origin}/#website`,
    url: origin,
    name: siteName,
    publisher: { "@id": `${origin}/#organization` },
  };
}
